import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import { useLayoutEffect, useRef, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { fileActionApi } from '../FileActions/fileActionConfig'
import { FileBreadCrumbContainer } from '../FileBreadCrumb/FileBreadCrumbContainer'
import { FileBreadCrumbSearch } from '../FileBreadCrumb/FileBreadCrumbSearch'
import { FileConflictDialog } from '../FileConflictDialog/FileConflictDialog'
import { FileRouteLink } from '../FileRouteLink/FileRouteLink'
import { FileSearch } from '../FileSearch/FileSearch'
import { FileSortMenu } from '../FileSortMenu/FileSortMenu'
import { FileContextProvider, useFileStore } from '../FileStore/FileContext'
import styles from './FileLayout.module.scss'
import { FileUpload } from './FileUpload'
import SvgListDashes from '@jotta/ui/SvgListDashes'
import SvgGrid from '@jotta/ui/SvgGrid'
import SvgArrowsDownUp from '@jotta/ui/SvgArrowsDownUp'
import { emptyTrashMutationKey } from '@jotta/grpc-connect-client/file/useTrash'
import { useIsMutating } from '@tanstack/react-query'

export const FileLayout = observer(function FileLayout() {
  useLayoutEffect(() => {
    console.group(`FileLayout ${location.pathname}`)
    return () => {
      console.groupEnd()
    }
  }, [])
  const { query = '' } = useParams()

  const ref = useRef<HTMLDivElement | null>(null)
  const store = useFileStore()
  const route = store.route
  const folder = store.folder
  const { isSearch } = route
  const [open, setOpen] = useState(false)
  const isEmptyTrashOngoing =
    useIsMutating({
      mutationKey: emptyTrashMutationKey,
    }) > 0
  return (
    <div
      ref={ref}
      className={styles.container}
      sx={{
        '.fileicon': {
          variant: 'styles.fileIcon',
        },
      }}
    >
      <AppLayoutPortal slot="header">
        <FileSearch open={open} setOpen={setOpen} />
      </AppLayoutPortal>
      <div className={styles.breadcrumb}>
        {isSearch ? (
          <FileBreadCrumbSearch
            handleCloseSearchInput={() => setOpen(false)}
            query={query}
          />
        ) : (
          <FileBreadCrumbContainer />
        )}
        <div className={styles.actions}>
          {route.isTrash && (
            <button
              className={clsx(styles.action, styles.trash)}
              data-testid="EmptyTrashButton"
              disabled={!folder.children.length || isEmptyTrashOngoing}
              onClick={() =>
                folder.actions.dispatch(
                  'EmptyTrash',
                  'FOLDER_LIST',
                  route.apiPath,
                )
              }
            >
              <PlainBrandIcon icon={fileActionApi.EmptyTrash.icon} />
              <span>{fileActionApi.EmptyTrash.name}</span>
            </button>
          )}
          <FileSortMenu>
            <PlainBrandIcon
              icon={SvgArrowsDownUp}
              data-testid="FileSortMenuButton"
            />
          </FileSortMenu>
          {route.viewMode === 'list' && (
            <FileRouteLink
              className={styles.action}
              data-testid="ViewModeThumbsButton"
              viewMode="thumbs"
              replace={isSearch}
            >
              <PlainBrandIcon icon={SvgGrid} />
            </FileRouteLink>
          )}
          {route.viewMode === 'thumbs' && (
            <FileRouteLink
              className={styles.action}
              data-testid="ViewModeListButton"
              viewMode="list"
              replace={isSearch}
            >
              <PlainBrandIcon icon={SvgListDashes} />
            </FileRouteLink>
          )}
        </div>
      </div>
      <FileUpload folder={folder} />
      <FileConflictDialog request={store.currentFileConflictRequest} />
      <Outlet />
    </div>
  )
})
/**
 * Export route layout for lazy loading by react-router
 * The naming is significant, see https://reactrouter.com/en/main/route/route#lazy
 */
export function Component() {
  return (
    <FileContextProvider>
      <FileLayout />
    </FileContextProvider>
  )
}
